// Render-parity checker. The client twin of the server's validateMatchState.
//
// After every fold the rendered SCENE (board meshes, hand fans, pile stacks,
// the solo turn pointer) must be the picture the authoritative mirror paints.
// Every recent live bug was exactly this class of failure and invisible to the
// server harness: a mesh-guard swallowing an avatar's SET_POSITION (avatar at
// world origin), a reload mounting cards on the wrong path, a mulligan prompt
// never surfacing. `checkRenderParity` asserts ONLY what the render pipeline
// actually guarantees (rebuildBoardFromMirror / applyZonesFromMirror /
// applyHandAdditionsFromMirror / reconcilePileRenderToMirror /
// applyTurnPhaseFromMirror), reusing the folds' own skip rules so a healthy
// scene is always clean:
//
//   I1 play-mesh-missing. Every mirror card in zone 'play' (identity known,
//                           catalog hit, not carried by a bearer) has a board
//                           mesh (rebuildBoardFromMirror's guarantee).
//   I2 play-position. That mesh's world position resolves (via the
//                           renderer's own host._cellAtWorld) to the card's
//                           authoritative cell. Catches avatar-at-origin.
//   I3 stray-board-mesh. No board mesh for a card the mirror puts in a pile
//                           or on the storyline (applyBoardExitsFromMirror's
//                           guarantee; zone 'hand' is exempt. An in-flight
//                           cast legitimately renders a mesh pre-confirm).
//   I4 hand. The opponent hand fan length equals handCounts
//                           (applyZonesFromMirror), and every own-seat mirror
//                           hand id that the hand folds would render (not a
//                           mesh, not unit-hand, not howl-held, not in a pile,
//                           catalog hit) IS in the rendered hand fan.
//   I5 piles. Opponent Spellbook/Atlas placeholder counts equal
//                           pileCounts; every identified mirror card whose zone
//                           maps to a visible pile is rendered in that pile,
//                           and no rendered pile card sits in a pile the mirror
//                           disputes (reconcilePileRenderToMirror's guarantee).
//   I6 solo-turn. Sandbox only: activePlayerNum is coherent with
//                           phase.currentTurn outside the mulligan-handoff
//                           window (applyTurnPhaseFromMirror's heal).
//   I7 prompt-parity. A mirror pendingChoice addressed to a locally
//                           visible seat (solo: either seat; harbinger prompts
//                           excluded. The ceremony overlay owns them) IS
//                           folded into host.state.pendingServerChoice
//                           (applyPendingChoiceFromMirror's guarantee), and a
//                           PILE prompt's picker surface is actually open
//                           (pendingPileSelect / the hand-discard glow. The
//                           fold re-attempts it every diff). Conversely a
//                           rendered picker whose prompt the mirror no longer
//                           holds is stale.
//   I8 unit-hand. Every mirror unitHands spell renders in the unit
//                           hand component (gameState.unitHands, the merge
//                           fold's guarantee) and NEVER in the player hand
//                           fan (the owner-locked-draw leak. The earlier
//                           live-bug class). Spells mid unit-hand drag or
//                           staged as a board mesh are the folds' own skips.
//   I9 controller/tap. A play-zone mesh's card instance carries the
//                           authoritative rotated (controller flip) + tapped
//                           flags (applyCardStateFromMirror's guarantee; the
//                           mesh orientation is derived from these via
//                           snapCardOrientation, so the flags ARE the
//                           renderer's rotation bookkeeping).
//   I10 counters. A play-zone mesh's card carries the authoritative
//                           counter total, and a counter-tracking card with a
//                           positive total has its level token mounted
//                           (host.counterHUDs. RefreshCounterHUD's registry).
//
// False-positive calibration: live play is full of legitimate sub-second races
// (an optimistic draw/cast whose confirming diff hasn't landed, a card mid
// flight to its pile). The reporter therefore never trusts a single sighting.
// a violation must survive a recheck CONFIRM_MS later (the confirming diff
// lands well inside that window) before it is reported. Reported once per
// violation key, toasted once per match, forwarded to the dev transcript.
// Dev builds only; a no-op in production.
import { toast } from 'sonner';
import { isDevBuild } from '../../../utils/devMode';
import { devLog } from '../../../utils/game/net/devLog';
import { zoneToPileName, ownSeatViews, isUnitHandSpell } from './authZones';
import { soloMulliganOpen } from './authTurnPhase';
import { cardUsesCounters } from '../../../utils/game/engine/automation';

const CONFIRM_MS = 2500;

// Session log of every CONFIRMED violation, across board mounts. The GUI
// flow suite (window.__valkenhallTest.parity) asserts this stays empty at
// each flow checkpoint. A page reload starts a fresh log.
const reportedViolationLog = [];
export function getReportedParityViolations() {
  return [...reportedViolationLog];
}

function cardIndex(host) {
  return host._sorceryCardIndex
    || (host._sorceryCardIndex = new Map((host.props?.sorceryCards || []).map((c) => [c.unique_id, c])));
}

// Pure detector: returns human-readable violation strings (empty = in parity).
// Each string starts with a stable `kind:id` key so the reporter can dedupe.
export function checkRenderParity(host, mirrorState) {
  const violations = [];
  const cards = mirrorState?.cards;
  if (!host?.meshes || !host.state) return violations;
  const gs = host.state.gameState;
  const byId = cardIndex(host);
  const localSeat = host.state.isHost ? 'p1' : 'p2';
  const localRotated = !host.state.isHost;
  // A cursor-owned mesh: a normal multi-member drag OR the combat defender
  // drag ({ combatDefenderCard, mesh }. No members array).
  const isDragged = (mesh) => !!host.dragging?.members?.some?.((dm) => dm.mesh === mesh)
    || host.dragging?.mesh === mesh;
  // A card claimed by EITHER side's presence stream (local pickup or the
  // opponent's live drag relayed via card:claim/stream): the stream owns its
  // pose until release, exactly the skip applyLivePositionsFromMirror uses.
  const isClaimed = (id) => !!(host.ownership && !host.ownership.is(id, 'free'));

  if (cards) {
    // Carried cards render on their bearer (equipment panel), never as a loose
    // mesh. The same exclusion rebuildBoardFromMirror applies.
    const carriedIds = new Set();
    for (const mc of Object.values(cards)) {
      if (!Array.isArray(mc?.equippedCards)) continue;
      for (const eq of mc.equippedCards) if (eq?.id) carriedIds.add(eq.id);
    }

    for (const [id, mc] of Object.entries(cards)) {
      if (mc?.zone !== 'play' || mc.cardId == null) continue;
      if (carriedIds.has(id)) continue;
      if (!byId.get(mc.cardId)) continue; // catalog miss. The rebuild skips it too
      const mesh = host.meshes.get(id);
      if (!mesh) {
        // I1. A play-zone card the board doesn't render.
        violations.push(`play-mesh-missing:${id} in-play card (cardId=${mc.cardId}) has no board mesh`);
        continue;
      }
      // I9. The card instance's controller/tap flags match the mirror
      // (applyCardStateFromMirror folds both; snapCardOrientation derives the
      // mesh orientation from them, so the flags are the render truth). A
      // mid-drag card is skipped. The cursor owns its pose.
      const inst = mesh.userData?.cardInstance;
      if (inst && !isDragged(mesh)) {
        if (typeof mc.rotated === 'boolean' && !!inst.rotated !== mc.rotated) {
          violations.push(`controller-flip:${id} card renders rotated=${!!inst.rotated} but server says ${mc.rotated}`);
        }
        // A chosen defender/interceptor's declaration tap is paid locally at
        // drop/click time and only commits with the confirm intent, so an
        // open combat selection legitimately renders ahead of the mirror for
        // as long as the player deliberates (refunded on Deselect). Same
        // class of skip as a dragged mesh.
        const pendingDeclarationTap = host._combatSelect?.chosen?.has?.(id);
        if (typeof mc.tapped === 'boolean' && !!inst.tapped !== mc.tapped && !pendingDeclarationTap) {
          violations.push(`tap-state:${id} card renders tapped=${!!inst.tapped} but server says ${mc.tapped}`);
        }
        // I10. Authoritative counter total, and the level token for a
        // counter-tracking card with a positive count (refreshCounterHUD
        // registry. The fold's _refreshCounterAnchorFor guarantee).
        if (typeof mc.counters === 'number') {
          if ((inst._counters || 0) !== mc.counters) {
            violations.push(`counter-state:${id} card renders ${inst._counters || 0} counters but server says ${mc.counters}`);
          } else if (mc.counters > 0 && host.counterHUDs
            && typeof host._getFullCard === 'function'
            && cardUsesCounters(host._getFullCard(mc.cardId))
            && !host.counterHUDs.has(id)) {
            violations.push(`counter-hud-missing:${id} ${mc.counters} counters but no level token mounted`);
          }
        }
      }
      // I2. The mesh's board position resolves to the authoritative cell.
      // Position is a pure function of (server cell, focusedCell). The
      // per-cell cascade at rest, the lifted fan when the cell is focused.
      // so an AT-REST mesh must always sit on its cell. Two derived states
      // legitimately lift a mesh off it: an active drag (the cursor owns the
      // pose) and membership in the FOCUSED cell's fan (state-derived, not a
      // mesh flag. The fan may spread past the cell bounds).
      if (typeof mc.cell?.col === 'number' && typeof mc.cell?.row === 'number'
        && typeof host._cellAtWorld === 'function'
        && typeof mesh.position?.x === 'number' && typeof mesh.position?.z === 'number'
        && !isDragged(mesh) && !isClaimed(id)
        && host.state?.focusedCell !== `${mc.cell.col},${mc.cell.row}`) {
        // An Aura sits on an intersection/border. Its canonical cell names a
        // covered square, so only its explicit anchor position can verify it.
        const aura = (mc.type ?? byId.get(mc.cardId)?.type) === 'Aura'
          && Number.isFinite(mc.pos?.x) && Number.isFinite(mc.pos?.z);
        if (aura) {
          if (Math.hypot(mesh.position.x - mc.pos.x, mesh.position.z - mc.pos.z) > .25) {
            violations.push(`play-position:${id} Aura mesh differs from its server anchor`);
          }
        } else {
          const at = host._cellAtWorld(mesh.position.x, mesh.position.z);
          if (!at || at.col !== mc.cell.col || at.row !== mc.cell.row) {
            violations.push(`play-position:${id} mesh at (${mesh.position.x.toFixed(2)}, ${mesh.position.z.toFixed(2)}) `
              + `resolves to ${at ? `{${at.col},${at.row}}` : 'no cell'} but server cell is {${mc.cell.col},${mc.cell.row}}`);
          }
        }
      }
    }

    // I3. A board mesh for a card the mirror says is NOT in play. Zone 'hand'
    // is exempt: a mesh + zone 'hand' is an in-flight cast on either seat (the
    // actor's optimistic mesh / the relay-staged copy) and the exit fold owns
    // its confirm-or-bounce. Exit flights don't false-positive here:
    // sendCardToPile deletes the mesh from host.meshes synchronously at entry.
    for (const [id, mesh] of host.meshes) {
      const mc = cards[id];
      if (!mc || !mc.zone || mc.zone === 'play' || mc.zone === 'hand') continue;
      if (isDragged(mesh)) continue;
      violations.push(`stray-board-mesh:${id} board mesh rendered but server zone is '${mc.zone}'`);
    }

    // I3b. Solo only: a board mesh the mirror has never heard of AT ALL. In
    // solo every board card is server-minted (the pilot owns both seats), so
    // an id absent from the projection can only be a phantom local spawn.
    // e.g. a resume mount's freshly spawned avatar stacked under its
    // server-restored twin (scrubUnknownBoardMeshesFromMirror's guarantee).
    // The two-sighting confirm window absorbs a fresh-boot Full racing the
    // client's own deck spawn.
    if (host.props?.isCasualSandbox) {
      for (const [id, mesh] of host.meshes) {
        if (cards[id]) continue;
        if (isDragged(mesh)) continue;
        const c = mesh.userData?.cardInstance;
        violations.push(`unknown-board-mesh:${id} board mesh (${c?.name || 'unnamed'}) has no entry in the server mirror`);
      }
    }
  }

  // I4a. Online: the opponent hand fan is exactly handCounts[oppSeat]
  // (applyZonesFromMirror rebuilds the placeholder fan to that length).
  if (!host.props?.isCasualSandbox) {
    const oppSeat = host.state.isHost ? 'p2' : 'p1';
    const target = mirrorState?.handCounts?.[oppSeat];
    if (typeof target === 'number') {
      const rendered = host.state.opponentHand?.length ?? 0;
      if (rendered !== target) {
        violations.push(`hand-count:${oppSeat} opponent hand fan renders ${rendered} cards, server says ${target}`);
      }
    }
  }

  // I4b. Own seat(s): every mirror hand id the hand folds would render is in
  // the rendered fan. Reuses the folds' own skip set (board mesh = in-flight
  // play, unit-hand spell, howl hold, catalog miss, the other solo hand) so
  // anything left over is a genuine render gap. A hand id still rendered in a
  // PILE is deliberately NOT skipped: the hand fold self-heals that (drops the
  // stale pile render + adds the card), so post-fold it is a real violation.
  // the old matching skip made the draw-vanishes bug invisible to this checker.
  if (cards) {
    for (const { seat, handKey } of ownSeatViews(host)) {
      const mirrorHand = mirrorState?.hands?.[seat];
      if (!Array.isArray(mirrorHand)) continue;
      const haveIds = new Set((host.state[handKey] || []).map((c) => c.id));
      const otherKey = handKey === 'handCards' ? 'handCards2' : 'handCards';
      for (const id of mirrorHand) {
        if (haveIds.has(id)) continue;
        if (host.meshes.has?.(id)) continue;
        if (host._howlHoldIds?.has(id)) continue;
        if (host._drawAnimIds?.has?.(id)) continue;
        if (isUnitHandSpell(id, mirrorState, gs)) continue;
        if (host.props?.isCasualSandbox && (host.state[otherKey] || []).some((c) => c.id === id)) continue;
        const mc = cards[id];
        if (!mc || mc.cardId == null || !byId.get(mc.cardId)) continue;
        violations.push(`hand-missing:${seat}:${id} in server hand (${seat}) but not rendered anywhere`);
      }
    }
  }

  // I5a. Online: the opponent's placeholder Spellbook/Atlas stacks are sized
  // to pileCounts (applyZonesFromMirror's guarantee).
  if (!host.props?.isCasualSandbox && gs?.piles) {
    const oppSeat = host.state.isHost ? 'p2' : 'p1';
    const oppRotated = !!host.state.isHost;
    const oppPiles = mirrorState?.pileCounts?.[oppSeat];
    if (oppPiles) {
      for (const [pileName, key] of [['Spellbook', 'spellbook'], ['Atlas', 'atlas']]) {
        const target = oppPiles[key];
        if (typeof target !== 'number') continue;
        const pile = gs.piles.find((p) => p.name === pileName && !!p.rotated === oppRotated);
        if (pile && pile.cards.length !== target) {
          violations.push(`pile-count:${oppSeat}:${key} rendered ${pile.cards.length} cards, server says ${target}`);
        }
      }
    }
  }

  if (cards && gs?.piles) {
    // I5b. Every identified mirror card whose zone maps to a visible pile is
    // rendered in that pile (reconcilePileRenderToMirror's ADD guarantee).
    // A card still holding a board mesh is mid board-exit. The reconciler
    // defers it, so we do too.
    for (const [id, mc] of Object.entries(cards)) {
      if (!mc?.zone || mc.cardId == null) continue;
      if (host.meshes.has(id)) continue;
      if (!byId.get(mc.cardId)) continue; // catalog miss. The renderer never renders blanks
      const rotated = (mc.owner === localSeat) ? localRotated : !localRotated;
      const name = zoneToPileName(mc.zone, rotated);
      if (!name) continue; // not a pile zone (play / hand / storyline / tokens)
      const pile = gs.piles.find((p) => p.name === name && !!p.rotated === rotated);
      if (!pile || !pile.cards.some((c) => c.id === id)) {
        violations.push(`pile-missing:${id} server zone '${mc.zone}' but not rendered in ${name}`);
      }
    }

    // I5c. No rendered pile card sits in a pile the mirror disputes
    // (reconcilePileRenderToMirror's REMOVE guarantee). Cards the mirror has
    // no opinion on (local-only, no zone) are left alone, like the reconciler.
    for (const pile of gs.piles) {
      if (!Array.isArray(pile?.cards)) continue;
      for (const c of pile.cards) {
        const mc = cards[c.id];
        if (!mc || !mc.zone) continue;
        if (zoneToPileName(mc.zone, !!pile.rotated) !== pile.name) {
          violations.push(`pile-stray:${c.id} rendered in ${pile.name} but server zone is '${mc.zone}'`);
        }
      }
    }
  }

  // I7. Prompt parity. A durable server prompt addressed to a locally
  // visible seat MUST have folded into the local picker slot
  // (applyPendingChoiceFromMirror), and a PILE prompt's dialog surface must
  // actually be open. The "prompt open server-side, nothing rendered" class.
  // Harbinger prompts render via the ceremony overlay, never the pickers.
  {
    const pc = mirrorState?.pendingChoice || null;
    const psc = host.state.pendingServerChoice || null;
    const visible = pc && pc.promptKind !== 'harbinger'
      && (host.props?.isCasualSandbox || pc.seat === localSeat);
    if (visible) {
      if (!psc || psc.promptId !== pc.promptId) {
        violations.push(`prompt-missing:${pc.promptId} server ${pc.kind || 'target'} prompt for ${pc.seat} has no rendered picker`);
      } else if (psc.kind === 'pile') {
        const surfaceOpen = pc.zone === 'hand'
          ? !!host._pendingDiscardCost
          : !!host.state.pendingPileSelect;
        if (!surfaceOpen) {
          violations.push(`prompt-surface-missing:${pc.promptId} pile prompt folded but its picker dialog is not open`);
        }
      }
    } else if (psc) {
      // No visible mirror prompt backs the rendered picker. A stale modal.
      violations.push(`prompt-stale:${psc.promptId} picker rendered but the server holds no matching pendingChoice`);
    }
  }

  // I8. Unit-hand parity. Every authoritative owner-locked spell renders in
  // the unit-hand component and never leaks into the player hand fan. Reuses
  // the folds' own skips: a spell staged as a board mesh (in-flight cast) or
  // mid unit-hand drag is legitimately out of the rendered hand.
  if (mirrorState?.unitHands) {
    const gsUnitHands = gs?.unitHands || {};
    const fanIds = new Set([
      ...(host.state.handCards || []).map((c) => c.id),
      ...(host.state.handCards2 || []).map((c) => c.id),
    ]);
    for (const [ownerId, entry] of Object.entries(mirrorState.unitHands)) {
      const spells = entry?.spells || [];
      const rendered = gsUnitHands[ownerId];
      const renderedIds = new Set((rendered?.spells || []).map((s) => s.id));
      for (const s of spells) {
        if (!s?.id) continue;
        if (fanIds.has(s.id)) {
          violations.push(`unit-hand-leak:${s.id} owner-locked spell rendered in the player hand fan`);
          continue;
        }
        if (renderedIds.has(s.id)) continue;
        if (host.meshes.has?.(s.id)) continue; // staged on the table (in-flight cast)
        if (host._unitHandDrag?.spellId === s.id) continue; // mid drag out of the hand
        violations.push(`unit-hand-missing:${ownerId}:${s.id} server unit-hand spell not rendered in the unit hand`);
      }
    }
  }

  // I6. Solo: the pilot's acting-seat pointer follows the authoritative turn
  // seat, except while a mulligan hand-off legitimately parks it on the
  // non-turn seat (the same carve-out applyTurnPhaseFromMirror heals around).
  const currentTurn = mirrorState?.phase?.currentTurn;
  if (host.props?.isCasualSandbox && currentTurn !== undefined && currentTurn !== null && !soloMulliganOpen(host)) {
    const want = currentTurn === 'p2' ? 2 : 1;
    if (host.state.activePlayerNum !== want) {
      violations.push(`solo-turn: activePlayerNum=${host.state.activePlayerNum} incoherent with server currentTurn='${currentTurn}'`);
    }
  }

  return violations;
}

// The stable dedupe key is everything before the first space (`kind:id`).
function keyOf(violation) {
  return violation.split(' ')[0];
}

// Fold-pipeline hook: coalesces per-fold requests and defers the check to
// after-render (rAF. Preact's batched setState microtasks have flushed by
// then) so it sees the post-fold scene, not a half-applied one.
export function scheduleRenderParityCheck(host) {
  if (!isDevBuild() || host._unmounted) return;
  // Replay playback: seeks emit Full snapshots that legitimately teleport
  // cards across zones between rechecks, and the omniscient state breaks the
  // live-match assumptions these invariants encode. The checker guards the
  // LIVE pipeline; replay renders through the same folds it already verified.
  if (host.props?.replayDriver) return;
  if (host._renderParityQueued) return;
  host._renderParityQueued = true;
  const run = () => {
    host._renderParityQueued = false;
    runRenderParity(host);
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
  else setTimeout(run, 0);
}

function runRenderParity(host) {
  if (host._unmounted) return;
  const m = host._authMirror?.getState?.();
  if (!m) return;
  const violations = checkRenderParity(host, m);

  // Two-sighting confirm: a violation is only REPORTED once it has survived a
  // recheck CONFIRM_MS after it was first seen. In-flight optimistic races
  // (a cast/draw awaiting its confirming diff, a card mid-flight) heal
  // themselves inside that window and never surface.
  const now = Date.now();
  const suspects = host._renderParitySuspects || (host._renderParitySuspects = new Map());
  const currentKeys = new Set(violations.map(keyOf));
  for (const k of [...suspects.keys()]) if (!currentKeys.has(k)) suspects.delete(k);

  const confirmed = [];
  let pending = false;
  for (const v of violations) {
    const firstSeen = suspects.get(keyOf(v));
    if (firstSeen == null) {
      suspects.set(keyOf(v), now);
      pending = true;
    } else if (now - firstSeen >= CONFIRM_MS) {
      confirmed.push(v);
    } else {
      pending = true;
    }
  }
  // Unconfirmed suspects need a recheck even if no further fold arrives.
  if (pending && !host._renderParityRecheckTimer) {
    host._renderParityRecheckTimer = setTimeout(() => {
      host._renderParityRecheckTimer = null;
      runRenderParity(host);
    }, CONFIRM_MS + 250);
  }
  if (!confirmed.length) return;

  const reported = host._renderParityReported || (host._renderParityReported = new Set());
  const fresh = confirmed.filter((v) => !reported.has(keyOf(v)));
  if (!fresh.length) return;
  for (const v of fresh) reported.add(keyOf(v));
  reportedViolationLog.push(...fresh);

  console.error(`[render-parity] ${fresh.length} violation(s):\n  ${fresh.join('\n  ')}`);
  devLog('renderParity', { violations: fresh });
  if (!host._renderParityToasted) {
    host._renderParityToasted = true;
    toast.error('Render parity violation (dev). Scene diverged from the server mirror, see console');
  }
}
